import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';

import { API_REDUCER_PATH, BASE_URL_V2, RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import { anonymousId } from 'src/common/tracking/data';

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL_V2,
});

let redirectNavigationInProgress = false;
const waitForRedirectNavigation = (url: string | URL) => {
  if (typeof window === 'undefined') return; // SSR safety
  const targetPath = new URL(url, window.location.origin).pathname;
  const isOnTargetPage = window.location.pathname === targetPath;
  // Already redirecting or already on the target page
  if (redirectNavigationInProgress || isOnTargetPage) return;

  redirectNavigationInProgress = true;
  window.location.replace(url);
  // Execution effectively stops here since the page unloads
  return new Promise<never>(() => {});
};

const baseQueryWithErrorHandlingAndRedirectNavigation: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const normalizedArgs = typeof args === 'string' ? { url: args } : { ...args };
  void anonymousId; // Guarantees cookie `thm-aid` is set, and doesn't get overwritten by backend

  const method = (normalizedArgs.method ?? 'GET').toUpperCase();
  const existingHeaders = normalizedArgs.headers;

  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const token = await csrfTokenPromise;
    normalizedArgs.headers = {
      ...existingHeaders,
      'csrf-token': token,
    };
  }

  const result = await baseQuery(normalizedArgs, api, extraOptions);
  if (result?.error) {
    if (typeof args === 'object' && args.url.startsWith('tutor/')) {
      return result;
    }
    if (result.error.status === 500) {
      Sentry.captureException('API 500 Error', { extra: { contexts: { 'API Response Data': result.error.data } } });
      await waitForRedirectNavigation('/r/500');
    } else if (result.error.status === 403) {
      const errorMessage = (result?.error?.data as { message: string }).message;
      if (errorMessage === 'User is banned') {
        await waitForRedirectNavigation('/banned');
      }
    } else if (result.meta?.response?.redirected) {
      await waitForRedirectNavigation(result.meta.response.url);
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: API_REDUCER_PATH,
  baseQuery: baseQueryWithErrorHandlingAndRedirectNavigation,
  endpoints: () => ({}),
  tagTypes: Object.values(RTKQueryTag),
});

const getCsrfToken = async () => {
  if (process.env.NODE_ENV === 'test' || process.env.REACT_APP_MOCK) return 'mock-csrf-token';
  const response = await fetch(`${BASE_URL_V2}auth/csrf`);
  if (response.redirected) {
    await waitForRedirectNavigation(response.url);
  }
  const data = (await response.json()) as ApiResponse<{ token: string }>;
  if (!data.data?.token) throw new Error('Failed to fetch CSRF token');
  const { token } = data.data;
  return token;
};
export const csrfTokenPromise = getCsrfToken();
